/** Obstacle Spawn mutation — places 1-3 unmatchable obstacle bubbles adjacent to existing bubbles */

import type { PRNG } from '../../rng.js';
import type { BoardState, MutationCard, Bubble } from '../../types.js';
import { coordToKey, hexNeighbors, isValidCoord } from '../../grid/hex.js';

export function applyObstacleSpawn(
  board: BoardState,
  _card: MutationCard,
  rng: PRNG,
): BoardState {
  const count = rng.nextInt(1, 3);

  // Collect empty cells that neighbor at least one existing bubble.
  // Without this, obstacles land in empty space, register as floating,
  // and get auto-dropped on the next shot.
  const candidates: Array<{ q: number; r: number }> = [];
  const seen = new Set<string>();
  for (const [, bubble] of board.bubbles) {
    for (const n of hexNeighbors(bubble.coord)) {
      const key = coordToKey(n);
      if (seen.has(key)) continue;
      seen.add(key);
      if (!isValidCoord(n, board.width, board.ceilingRow)) continue;
      if (board.bubbles.has(key)) continue;
      candidates.push(n);
    }
  }

  if (candidates.length === 0) return board;

  let result = board;

  for (let i = 0; i < count && candidates.length > 0; i++) {
    const idx = rng.nextInt(0, candidates.length - 1);
    const cell = candidates[idx]!;
    const key = coordToKey(cell);

    const obstacle: Bubble = {
      coord: cell,
      color: board.activeColors[0]!, // Color is irrelevant for obstacles
      isWild: false,
      isBomb: false,
      isObstacle: true,
    };

    const newBubbles = new Map(result.bubbles);
    newBubbles.set(key, obstacle);
    result = { ...result, bubbles: newBubbles };

    candidates.splice(idx, 1);
  }

  return result;
}
